/**
 * Constructs an array of full resource URLs from the load configuration.
 * 
 * Syntax:
 *   { endpoint: 'invoicePayment', filter: { invoiceId: '$id', paid: false } }
 *   - $prefix indicates a reference (e.g., '$id' = form's id)
 *   - Generates: 'invoicePayment?filter=invoiceId:123;paid:false'
 *   { endpoint: 'expense/$id', single: true }
 *   - '$id' in the path is the form's id too: 'expense/123'. `single` is read
 *     by the caller (StateFormView), which wraps the one record as a list.
 *
 * @param {Array<Object>} loadConfigArray - Array of load configuration objects.
 * @param {string | number | undefined | null} id - The ID value to substitute into endpoints.
 * @returns {Array<string>} An array of fully constructed resource URLs.
 */
function getLoadUrls (loadConfigArray, id) {
    if (!Array.isArray(loadConfigArray) || loadConfigArray.length === 0) {
        return [];
    }

    const idString = String(id);
    const hasId = id !== undefined && id !== null && idString !== 'undefined' && idString !== 'null';

    return loadConfigArray.map(loadCfg => {
        let resource = loadCfg.endpoint || '';

        // `$id` in the path itself — 'expense/$id' reads the one record
        if (hasId) resource = resource.replace('$id', idString);

        // Process filter object if present
        if (loadCfg.filter && typeof loadCfg.filter === 'object') {
            const filterParts = [];
            
            Object.entries(loadCfg.filter).forEach(([key, value]) => {
                // Handle $prefix references (e.g., '$id' -> form's id)
                if (typeof value === 'string' && value.startsWith('$')) {
                    const refKey = value.slice(1); // Remove $ prefix
                    if (refKey === 'id' && hasId) {
                        filterParts.push(`${key}:${idString}`);
                    }
                    // Future: could support other references like '$userId', '$orgId', etc.
                } else {
                    // Literal value (boolean, number, string)
                    filterParts.push(`${key}:${value}`);
                }
            });

            if (filterParts.length > 0) {
                const filterString = filterParts.join(';');
                const separator = resource.includes('?') ? '&' : '?';
                resource += `${separator}filter=${filterString}`;
            }
        }
        
        return resource;
    }).filter(url => url !== ''); 
}

// --- Example Usage ---

const loadConfig = [
    // Filter with $id reference
    {
        endpoint: 'invoicePayment',
        filter: { invoiceId: '$id', paid: false }
    },
    // Simple filter with literal values
    {
        endpoint: 'paymentAccount/all/paymentTransaction',
        filter: { isPositive: true, fullyMatched: false },
    },
    // No filter needed
    {
        endpoint: 'users/list',
    },
];

const invoiceId = 123;

const urls = getLoadUrls(loadConfig, invoiceId);

/*
[
  'invoicePayment?filter=invoiceId:123;paid:false',
  'paymentAccount/all/paymentTransaction?filter=isPositive:true;fullyMatched:false',
  'users/list',
]
*/

/**
 * Maps query results to a state object based on an array of target keys.
 *
 * @param {Array<string>} targets - Array of state keys (e.g., ['key1', 'key2']).
 * @param {Array<Object>} results - Array of result objects from useQueries.
 * @returns {Object} An object structured for setState: { [target]: data, ... }.
 */
const mapResultsToState = (targets, results) => {
    // Guard against null/undefined inputs
    if (!Array.isArray(targets) || !Array.isArray(results)) {
        console.error("Targets and Results must be arrays", {
            targetsType: Array.isArray(targets) ? 'array' : typeof targets,
            resultsType: Array.isArray(results) ? 'array' : typeof results
        });
        return {};
    }
    
    // Basic length check for safety, though they should be the same length
    if (targets.length !== results.length) {
        console.error("Targets array and Results array length mismatch. Check configuration.");
        return {};
    }

    return targets.reduce((acc, target, index) => {
        const result = results[index];

        // Ensure the query succeeded before trying to access .data
        if (result && result.isSuccess) {
            // Check for the standard API shape ({ data: payload }) and use it if present
            const res = result.data;
            // Handle null/undefined data gracefully - default to empty array or null
            const payload = res?.data !== undefined ? res.data : (res ?? null);
            
            // Build the key-value pair for the state object
            acc[target] = payload;
        } 
        // If the query failed or is still loading, we skip setting that specific key.
        
        return acc;
    }, {}); // Start with an empty object
};

export { getLoadUrls, mapResultsToState };
